import { Component, computed, inject, input, signal, effect, DestroyRef } from '@angular/core';
import { currentTenantId } from '@/utils/admin-utils';
import { ProfiNeoSyncStateService, ProfiNeoSyncStateItemDto } from '@artemis-software/wr-api';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import { NbToastrService, NbComponentStatus } from '@nebular/theme';
import { firstValueFrom, map } from 'rxjs';

@Component({
  standalone: false,
  selector: 'wr-building-profi-neo-sync-page',
  templateUrl: './building-profi-neo-sync-page.component.html',
  styleUrls: ['./building-profi-neo-sync-page.component.scss']
})
export class BuildingProfiNeoSyncPageComponent {
  currentTenantId = currentTenantId();
  readonly profiNeoSyncStateService = inject(ProfiNeoSyncStateService);
  readonly nbToastrService = inject(NbToastrService);
  private readonly destroyRef = inject(DestroyRef);

  readonly buildingId = input.required<string | null>();
  private readonly refreshTrigger = signal(0);
  readonly syncInProgress = signal(false);

  readonly statusColorMap: Record<ProfiNeoSyncStateItemDto.SyncStatusEnum, NbComponentStatus> = {
    PENDING: 'warning',
    QUEUED: 'info',
    COMPLETED: 'success',
    FAILED: 'danger',
  } as const;

  profiNeoSyncState = asyncComputed(() => {
    const buildingId = this.buildingId();
    this.refreshTrigger();
    if (!buildingId) return undefined;
    return this.profiNeoSyncStateService.findAll({
      buildingId: buildingId,
      internalTenantId: this.currentTenantId(),
      syncType: ProfiNeoSyncStateItemDto.SyncTypeEnum.Legacy,
      size: 1,
      sort: {
        sort: 'created',
        order: 'DESC'
      }
    }).pipe(
      map(syncStateList => syncStateList?.[0])
    );
  });

  readonly isSynced = computed(() => {
    const syncStatus = this.profiNeoSyncState()?.syncStatus;
    return syncStatus === 'COMPLETED';
  });

  readonly isProcessing = computed(() => {
    const syncStatus = this.profiNeoSyncState()?.syncStatus;
    return syncStatus === 'PENDING' || syncStatus === 'QUEUED';
  });

  readonly canShowSyncButton = computed(() => {
    const syncStatus = this.profiNeoSyncState()?.syncStatus;
    return !syncStatus || syncStatus === 'FAILED';
  });

  constructor() {
    effect(onCleanup => {
      if (!this.isProcessing()) return;
      const intervalId = setInterval(() => {
        this.refreshTrigger.update(previousRefreshCount => previousRefreshCount + 1);
      }, 4000);
      onCleanup(() => clearInterval(intervalId));
    });

    this.destroyRef.onDestroy(() => {
    });
  }

  async synchronizeBuilding() {
    const buildingId = this.buildingId();
    if (!buildingId) {
      this.nbToastrService.danger('Gebäude konnte nicht gefunden werden', 'Gebäude');
      return;
    }
    try {
      this.syncInProgress.set(true);
      await firstValueFrom(this.profiNeoSyncStateService.merge({
        buildingId,
        syncType: ProfiNeoSyncStateItemDto.SyncTypeEnum.Legacy,
      }));
      this.nbToastrService.success('Synchronisierung gestartet', 'Gebäude');
      this.refreshTrigger.update(previousRefreshCount => previousRefreshCount + 1);
    } catch (error) {
      this.nbToastrService.danger('Fehler bei der Synchronisierung', 'Gebäude');
    } finally {
      this.syncInProgress.set(false);
    }
  }
}
